import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, UserPlus, Users, UserCheck, UserX } from 'lucide-react';

import MoradoresTable from './MoradoresTable';
import MoradorModal from './MoradorModal';
import { PageHeader, PageLayout, SearchField, SummaryCard, SummaryGrid } from '../../components/ui/PageLayout';
import api, { getApiErrorMessage } from '../../services/api';

const PAGE_SIZE = 25;

const MoradoresPage = ({
  authenticatedUser,
  onLogout
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoriaUnidadeId = searchParams.get('categoria_unidade_id') || '';
  const [unidades, setUnidades] = useState([]);
  const [unidadesError, setUnidadesError] = useState('');

  useEffect(() => {
    api.get('/unidades').then(({ data }) => setUnidades(data))
      .catch((error) => setUnidadesError(getApiErrorMessage(error, 'Não foi possível carregar as unidades.')));
  }, []);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [moradorSelecionado, setMoradorSelecionado] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [page, setPage] = useState(1);
  const [moradores, setMoradores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    total: 0,
    total_ativos: 0,
    total_inativos: 0,
    total_filtrado: 0,
    total_pages: 1,
  });

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
      setPage(1);
    }, 350);

    return () => window.clearTimeout(timeout);
  }, [searchTerm]);

  const fetchMoradores = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/moradores/paginado', {
        params: {
          page,
          limit: PAGE_SIZE,
          search: debouncedSearchTerm || undefined,
          status: statusFilter,
          categoria_unidade_id: categoriaUnidadeId || undefined,
        },
      });
      const data = response.data || {};
      const totalPages = data.total_pages || 1;

      setMoradores(data.items || []);
      setPagination({
        total: data.total || 0,
        total_ativos: data.total_ativos || 0,
        total_inativos: data.total_inativos || 0,
        total_filtrado: data.total_filtrado || 0,
        total_pages: totalPages,
      });

      if (page > totalPages) {
        setPage(totalPages);
      }
    } catch (error) {
      console.error('Erro ao buscar moradores:', error);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchTerm, page, statusFilter, categoriaUnidadeId]);

  useEffect(() => {
    fetchMoradores();
  }, [fetchMoradores]);

  const handleEditClick = (morador) => {
    setMoradorSelecionado(morador);
    setIsModalOpen(true);
  };

  const handleNovoMoradorClick = () => {
    setMoradorSelecionado(null);
    setIsModalOpen(true);
  };

  const handleCloseMoradorModal = async () => {
    setIsModalOpen(false);
    setMoradorSelecionado(null);
    await fetchMoradores();
  };

  const handleStatusFilterChange = (nextStatus) => {
    setStatusFilter((current) => {
      const resolved = current === nextStatus && nextStatus !== 'todos' ? 'todos' : nextStatus;
      return resolved;
    });
    setPage(1);
  };

  const totalMoradores = pagination.total;
  const moradoresAtivos = pagination.total_ativos;
  const moradoresInativos = pagination.total_inativos;
  const firstItem = pagination.total_filtrado ? (page - 1) * PAGE_SIZE + 1 : 0;
  const lastItem = Math.min(page * PAGE_SIZE, pagination.total_filtrado);
  const canGoPrevious = page > 1;
  const canGoNext = page < pagination.total_pages;

  return (
    <PageLayout>
      <PageHeader
        title="Moradores"
        description={`Gestao de moradores${authenticatedUser ? ` • Logado como ${authenticatedUser}` : ''}`}
        actions={(
          <>
            <button
              onClick={handleNovoMoradorClick}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-condo-dark px-5 py-2.5 text-white shadow-md transition hover:bg-slate-700 sm:w-auto"
            >
              <UserPlus size={20} /> Novo Morador
            </button>
            <button
              onClick={onLogout}
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-5 py-2.5 text-slate-700 transition hover:bg-slate-100 sm:w-auto"
            >
              Sair
            </button>
          </>
        )}
      />

      <SummaryGrid>
        <SummaryCard
          title="Total de Moradores"
          value={totalMoradores}
          color="blue"
          icon={<Users size={24} />}
          active={statusFilter === 'todos'}
          onClick={() => handleStatusFilterChange('todos')}
        />
        <SummaryCard
          title="Moradores Ativos"
          value={moradoresAtivos}
          color="green"
          icon={<UserCheck size={24} />}
          active={statusFilter === 'ativos'}
          onClick={() => handleStatusFilterChange('ativos')}
        />
        <SummaryCard
          title="Moradores Inativos"
          value={moradoresInativos}
          color="red"
          icon={<UserX size={24} />}
          active={statusFilter === 'inativos'}
          onClick={() => handleStatusFilterChange('inativos')}
        />
      </SummaryGrid>

      <SearchField
        value={searchTerm}
        onChange={(event) => setSearchTerm(event.target.value)}
        placeholder="Buscar morador por nome, CPF, unidade ou contato..."
      />

      <label className="block rounded-xl border border-slate-200 bg-white p-4 text-sm font-semibold text-slate-700">
        Unidade cadastrada
        <select aria-label="Filtrar por unidade" className="ml-3 rounded-lg border border-slate-200 bg-white px-4 py-2" value={categoriaUnidadeId} onChange={(event) => {
          const next = new URLSearchParams(searchParams);
          if (event.target.value) next.set('categoria_unidade_id', event.target.value);
          else next.delete('categoria_unidade_id');
          setPage(1); setSearchParams(next);
        }}>
          <option value="">Todas as unidades</option>
          {unidades.map((item) => <option key={item.id} value={item.id}>{item.nome}</option>)}
        </select>
        {unidadesError && <span role="alert" className="mt-2 block text-red-600">{unidadesError}</span>}
      </label>

      <MoradoresTable
        moradores={moradores}
        loading={loading}
        refreshData={fetchMoradores}
        onEdit={handleEditClick}
      />

      <div className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between">
        <span>
          Exibindo {firstItem}-{lastItem} de {pagination.total_filtrado} morador(es)
        </span>
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            disabled={!canGoPrevious || loading}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            title="Pagina anterior"
          >
            <ChevronLeft size={18} />
          </button>
          <span className="min-w-24 text-center font-semibold text-slate-700">
            Pagina {page} de {pagination.total_pages}
          </span>
          <button
            type="button"
            onClick={() => setPage((current) => Math.min(pagination.total_pages, current + 1))}
            disabled={!canGoNext || loading}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            title="Proxima pagina"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      <MoradorModal
        isOpen={isModalOpen}
        onClose={handleCloseMoradorModal}
        moradorParaEditar={moradorSelecionado}
      />
    </PageLayout>
  );
};

export default MoradoresPage;
